import flow from 'lodash/flow';
import { memo, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

// decorators
import withAuth from '../decorators/auth/withAuth';
import withBreakpoints from '../decorators/withBreakpoints';
import withCurrentRouteCustomProps from '../decorators/withCurrentRouteCustomProps';
import withDesktopApp from '../decorators/withDesktopApp';
import withMobileJourney from '../decorators/withMobileJourney';
import withContextProviders from '../decorators/withContextProviders';

// components

// actions
import { fetchMe } from '../actions/me';
import { updateMWBypassCookie } from '../actions/app';

// utils
import initPageViewAnalytics from '../utils/initPageViewAnalytics';
import { setCookie } from '../utils/cookie';
import connectAsync from '../utils/connectAsync';

// selectors
import { selectUserAgent } from '../selectors/app';
import { selectExperiment } from '../selectors/config';

// constants
import { mwLandingBypassCookie } from '../constants/cookies';
import { MW_LANDING_SHOW_AFTER_X_DAYS } from '../constants/experiments/mobileLanding';
import { ONETRUST_ENABLED } from '../constants/experiments/ads';
import { OneTrust } from './consent/OneTrust';
import AppRootLoader from './AppRootLoader';
import MobileWebEntryLoader from './MobileWebEntryLoader';
import withGuideItemProfile from './withGuideItemProfile';

// css
import '../styles/reset.css';
import '../styles/site.css';
import '../styles/fontFaceDeclarations.css';

const MOBILE_OVERLAY_EXPIRATION_DAYS = 1;

const AppConnector = memo((props) => {
  const {
    apiClient,
    location,
    store,
    match,
    route,
    routeProps,
    breakpoint,
    shouldShowMobileOverlay,
    history,
  } = props;

  const contentProps = {
    breakpoint,
    route,
    routeProps,
    location,
    apiClient,
    history,
  };

  const dispatch = useDispatch();
  const userAgent = useSelector(selectUserAgent);
  const mobileOverlayExpirationDaysOverride = useSelector((state) =>
    selectExperiment(state, MW_LANDING_SHOW_AFTER_X_DAYS),
  );
  const isOneTrustEnabled = useSelector((state) =>
    selectExperiment(state, ONETRUST_ENABLED),
  );

  const handleContinueClick = () => {
    const expDays =
      mobileOverlayExpirationDaysOverride || MOBILE_OVERLAY_EXPIRATION_DAYS;

    setCookie(mwLandingBypassCookie.name, expDays);
    dispatch(updateMWBypassCookie(true));
  };

  useEffect(() => {
    // using function for getState so that it always has access to latest consent setting
    initPageViewAnalytics(apiClient, store.getState, history);
  }, []); /* eslint-disable-line react-hooks/exhaustive-deps */

  const MobileWebLandingComponent = useMemo(
    () =>
      routeProps.guideContext.guideId
        ? withGuideItemProfile(MobileWebEntryLoader)
        : MobileWebEntryLoader,
    [routeProps.guideContext.guideId],
  );

  if (shouldShowMobileOverlay) {
    return (
      <>
        {isOneTrustEnabled && <OneTrust isMobileBlockerFlow />}
        <MobileWebLandingComponent
          routeProps={routeProps}
          match={match}
          isLanding={routeProps.isLanding}
          location={location}
          userAgent={userAgent}
          onContinueClick={handleContinueClick}
        />
      </>
    );
  }

  return <AppRootLoader {...contentProps} />;
});

AppConnector.propTypes = {
  apiClient: PropTypes.object,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  route: PropTypes.object.isRequired,
  routeProps: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  store: PropTypes.object,
  // withBreakpoints
  breakpoint: PropTypes.number.isRequired,
  // withMobileJourney
  shouldShowMobileOverlay: PropTypes.bool,
};

export default flow(
  withMobileJourney,
  withBreakpoints, // IMPORTANT: This needs to render after withCurrentRouteCustomProps
  withCurrentRouteCustomProps,
  withAuth,
  connectAsync([fetchMe]),
  withDesktopApp,
  withContextProviders,
)(AppConnector);
